/**
 * THE API CONTRACT THE UI CODES AGAINST.
 *
 * The API is not running yet, so this file is the written contract, not a guess made per screen.
 * Every type here mirrors something the server already owns:
 *
 *   DenyReason / AuthzStage / DenyBody  ←  packages/contracts/src/decision.ts
 *   SessionContext                      ←  packages/contracts/src/vocabulary.ts
 *   CompanyLifecycleStatus              ←  packages/contracts/src/enums.ts
 *   ModuleCode + status                 ←  packages/contracts/src/modules.ts
 *
 * They are re-declared rather than imported because `@glpoint/contracts` publishes from `dist/` and
 * this app must build with Vite from source alone. `src/api/contract.mirror.test.ts` reads the
 * contracts source files off disk and fails if this mirror ever drifts — the mirror is checked, not
 * trusted.
 */

// ── decision.ts ───────────────────────────────────────────────────────────────────────────────────

export const AUTHZ_STAGES = [
  'ROUTE',
  'AUTHN',
  'TENANT',
  'SUBSCRIPTION',
  'COMPANY',
  'MODULE',
  'CAPABILITY',
  'CSRF',
  'RATE',
] as const;
export type AuthzStage = (typeof AUTHZ_STAGES)[number];

export const DENY_REASONS = [
  'declaration_missing',
  'no_session',
  'session_expired',
  'session_revoked',
  'anonymous_not_permitted',
  'session_context_mismatch',
  'stepup_required',
  'no_tenant_context',
  'wrong_context',
  'platform_context_no_grant',
  'cross_tenant',
  'subscription_inactive',
  'subscription_trialing_restricted',
  'subscription_past_due',
  'subscription_suspended',
  'subscription_expired',
  'no_company_grant',
  'company_not_resolvable',
  'company_suspended',
  'company_archived',
  'company_review_required',
  'company_inactive',
  'not_subscribed',
  'sku_expired',
  'unknown_module',
  'missing_module',
  'company_not_assigned',
  'limit_reached',
  'missing_capability',
  'capability_wrong_context',
  'csrf_failed',
  'origin_not_allowed',
  'rate_limited',
  'predicate_error',
  'indeterminate',
] as const;
export type DenyReason = (typeof DENY_REASONS)[number];

/**
 * The body a denial produces (decision.ts `toDenyBody`).
 *
 * CF-37's two-layer contract, and it is the reason the UI has one denial component and not a `403`
 * page: outside the tenant `reason` and `stage` are ABSENT and the UI must say nothing more than the
 * uniform sentence. Inside the tenant the first failing stage's reason arrives and the UI owes the
 * user a written next step (09 §5.8).
 */
export interface DenyBody {
  readonly error: string;
  readonly reason?: DenyReason;
  readonly stage?: AuthzStage;
  readonly requestUid: string;
}

// ── vocabulary.ts ─────────────────────────────────────────────────────────────────────────────────

/**
 * 09 §"two named surfaces" / 04 I6: exactly one context is active per session, and it is a property
 * of the SESSION — never of the user, never of the request. PLATFORM and TENANT never bleed and are
 * never simultaneous.
 */
export const SESSION_CONTEXTS = ['PLATFORM', 'TENANT'] as const;
export type SessionContext = (typeof SESSION_CONTEXTS)[number];

// ── enums.ts ──────────────────────────────────────────────────────────────────────────────────────

/** 09 §5.1 / decision 10: a company's lifecycle state is DISPLAYED, never inferred. */
export const COMPANY_LIFECYCLE_STATUSES = [
  'prospect',
  'active',
  'dormant',
  'former_client',
  'merged_into',
  'quarantined',
  'source_unavailable',
  'review_required',
] as const;
export type CompanyLifecycleStatus = (typeof COMPANY_LIFECYCLE_STATUSES)[number];

export const CATALOGUE_STATUSES = ['active', 'planned', 'retired'] as const;
export type CatalogueStatus = (typeof CATALOGUE_STATUSES)[number];

// ── Resources the shell reads ─────────────────────────────────────────────────────────────────────

/**
 * The signed-in session as the server describes it.
 *
 * There is no client-side permission logic anywhere in this app. `capabilities` is here for UX
 * affordances ONLY — hiding a control the server would deny anyway. A hidden button is an
 * affordance, never a control: the gate decides, and it decides again on every request.
 */
export interface SessionSummary {
  readonly userUid: string;
  readonly displayName: string;
  readonly sessionContext: SessionContext;
  /** 09 §3.1: the משרד slot is shown only when the user belongs to more than one tenant. */
  readonly tenants: readonly TenantSummary[];
  readonly activeTenantUid: string | null;
  readonly capabilities: readonly string[];
  /** Non-null ⇒ a PLATFORM principal is inside a tenant under a support grant (04 §13.1.5). */
  readonly supportGrantUid: string | null;
}

export interface TenantSummary {
  readonly tenantUid: string;
  readonly displayName: string;
}

/**
 * One row of the single server-side "visible companies" answer (09 §5.1, CONFLICT C-1).
 * The picker never shows a company the user may not open and then denies on click.
 */
export interface CompanySummary {
  readonly companyUid: string;
  /** 09 §9.1 V1: the full legal name. Never a db code, never a FIN cid, never a unit slug. */
  readonly legalName: string;
  /** 09 §5.1: ח.פ as a muted second line. Nullable — a broken-identity row still appears. */
  readonly registrationNumber: string | null;
  readonly lifecycleStatus: CompanyLifecycleStatus;
  /** 09 §5.1 "broken identity": the row appears, marked, rather than silently vanishing. */
  readonly needsAttention: boolean;
}

/**
 * Module entitlement as the server resolved it — Subscription → SKU → sku_module → module_code.
 * The UI never resolves entitlement; it renders the answer.
 */
export interface ModuleEntitlement {
  readonly moduleCode: string;
  readonly displayNameHe: string;
  readonly catalogueStatus: CatalogueStatus;
  /** `true` only when the tenant bought it AND the server says this company is in scope. */
  readonly available: boolean;
  /** Present when `available` is false; drives the Hebrew explanation (09 §5.8). */
  readonly unavailableReason?: DenyReason;
}

/** 09 §5.2: the period picker expresses a period TYPE, not just a value (CONFLICT C-2). */
export type PeriodKind = 'year' | 'half' | 'quarter' | 'bimonthly' | 'month' | 'as_of';

export interface PeriodSummary {
  readonly periodUid: string;
  readonly kind: PeriodKind;
  /** Already resolved to business form by the server. 09 §5.2: never the raw source key. */
  readonly displayHe: string;
  /** 09 §5.2: a locked period is selectable and readable, and shows a lock chip. */
  readonly locked: boolean;
}

/**
 * One blocker on נייר השלמות (09 §5.7b). A blocked document is a checklist, not an error.
 * B2 is binding: every blocker carries a deep link to the exact place the value is entered.
 */
export interface DocumentBlocker {
  readonly blockerUid: string;
  /** In business Hebrew — "שיעור ריבית להלוואות לזמן ארוך", never a column name. */
  readonly whatIsMissingHe: string;
  /** B2: the deep link. A blocker with no link is a support call. */
  readonly whereEnteredHref: string;
  readonly whereEnteredLabelHe: string;
  readonly ownerDisplayName: string | null;
}

export interface BlockedDocument {
  readonly documentUid: string;
  readonly documentKindHe: string;
  readonly companyLegalName: string;
  readonly periodDisplayHe: string;
  readonly blockers: readonly DocumentBlocker[];
  /** B4: the draft is producible even when the final is blocked. */
  readonly draftAvailable: boolean;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// ADMINISTRATION VOCABULARY
//
// The Control Plane and the Tenant Admin screens speak the vocabulary the server already owns:
//
//   TENANT_STATUSES / SUBSCRIPTION_STATES / MEMBERSHIP_STATUSES / ACCESS_LEVELS
//                                        ←  packages/contracts/src/enums.ts
//   SEEDED_TENANT_ROLES / PLATFORM_ROLES ←  packages/contracts/src/capabilities.ts
//
// Same rule as above: re-declared because this app builds from source with Vite, and
// `contract.mirror.test.ts` reads the authoritative files off disk so the copy is checked.
// ══════════════════════════════════════════════════════════════════════════════════════════════════

export const TENANT_STATUSES = ['active', 'trial', 'suspended', 'closed'] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

export const SUBSCRIPTION_STATES = [
  'draft',
  'trialing',
  'active',
  'past_due',
  'suspended',
  'cancelled',
  'expired',
] as const;
export type SubscriptionState = (typeof SUBSCRIPTION_STATES)[number];

/** 01 §A3 — a status per MEMBERSHIP, never on the identity. */
export const MEMBERSHIP_STATUSES = ['invited', 'active', 'suspended', 'disabled'] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

/** 01 §A5 — read / write / approve. There is deliberately NO `deny`: exclusion is the absent row. */
export const ACCESS_LEVELS = ['read', 'write', 'approve'] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];

export const SEEDED_TENANT_ROLES = [
  'tenant_owner',
  'tenant_admin',
  'tenant_member',
  'tenant_billing_contact',
  'client_viewer',
] as const;
export type SeededTenantRole = (typeof SEEDED_TENANT_ROLES)[number];

export const PLATFORM_ROLES = [
  'platform_owner',
  'platform_operator',
  'platform_support',
  'platform_auditor',
] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

/** One row of the Control Plane's tenant list. Every count here was computed by the server. */
export interface TenantAdminSummary {
  readonly tenantUid: string;
  /** 09 §9.1 V1: the office's own legal name. Never a slug as the identity. */
  readonly legalName: string;
  /** The opaque handle it was provisioned under. A muted second line, never the identity. */
  readonly slug: string;
  readonly status: TenantStatus;
  readonly subscriptionState: SubscriptionState;
  readonly companyCount: number;
  readonly seatCount: number;
}

/** `apps/api/src/services.ts` MembershipView, in the shape a Hebrew screen needs. */
export interface MembershipSummary {
  readonly membershipUid: string;
  readonly displayName: string;
  /** Shown LTR inside an RTL row (09 §9.5 A1: bidi is controlled, never inferred). */
  readonly email: string;
  readonly status: MembershipStatus;
  /**
   * OA-0001 §3.7: an invitee arrives with NO capabilities, so this list is legitimately empty, and
   * an empty list is a STATE the screen writes out — never a blank cell.
   */
  readonly roleCodes: readonly string[];
  /** 01 §A3: `invited` and `active` consume a seat. Stated by the server, never derived here. */
  readonly consumesSeat: boolean;
}

/** `core.role` + `core.role_permission`, as the roles screen reads them. */
export interface RoleSummary {
  readonly roleCode: string;
  readonly displayNameHe: string;
  /** Capability codes. `capabilityDescriptionHe` turns each one into office Hebrew. */
  readonly capabilities: readonly string[];
  /** A role a tenant created for one person. Shown, because it is the one that drifts. */
  readonly isPersonal: boolean;
}

/**
 * THE FOUR ANSWERS A ROLE ASSIGNMENT CAN BE REFUSED WITH — OA-0001 §3.3.
 *
 * Mirrored from `apps/api/src` rather than from `packages/contracts`, because that is where they are
 * declared: the first two are `CeilingDecision.reason` in `domain/capability-ceiling.ts`, the last
 * two are the store outcomes of `assignRoles` in `services/customer-admin.ts`. The mirror is checked
 * against those files off disk, exactly as the contracts mirror is — a copy nobody verifies is how
 * two namespaces get invented (G-06).
 *
 * They are NOT deny reasons. The gate had already allowed the request when they were produced: the
 * caller holds `tenant.role.manage` and is inside their tenant. What these say is that the ROW may
 * not be written, which is a rule about a FUTURE request and not an access decision on this one —
 * the distinction `capability-ceiling.ts` opens with, and the reason they travel as a conflict and
 * never as a `DenyBody`.
 */
export const ROLE_ASSIGNMENT_REFUSALS = [
  'exceeds_granter_capabilities',
  'delegation_capability_not_delegatable',
  'unknown_role',
  'membership_not_found',
] as const;
export type RoleAssignmentRefusalReason = (typeof ROLE_ASSIGNMENT_REFUSALS)[number];

/**
 * The refusal as the SERVER produced it.
 *
 * `excess` is the list `checkDelegationCeiling` returns instead of a boolean, and it is the whole
 * point of the shape: *"you may not grant these three capabilities" is actionable in a way that
 * `403 forbidden` is not*. It carries capability CODES; `roleAssignmentText.ts` turns them into the
 * catalogue's Hebrew, and no screen ever renders one.
 *
 * Empty for `unknown_role` and `membership_not_found`, which are not about capabilities at all.
 */
export interface RoleAssignmentRefusal {
  readonly reason: RoleAssignmentRefusalReason;
  readonly excess: readonly string[];
}

/** One row of `core.user_company_access`. CG-2: there is no `deny` level and no `deny` row. */
export interface CompanyGrantSummary {
  readonly membershipUid: string;
  readonly companyUid: string;
  readonly accessLevel: AccessLevel;
  readonly grantedAtUtc: string;
  readonly expiresAtUtc: string | null;
}

// ── Commercial capacity (OA-0001 §3.11 / 08 §7) ───────────────────────────────────────────────────

export type CapacityKind = 'company' | 'seat' | 'module_company';

/**
 * UNMETERED IS A SHAPE, NOT A NULL — mirrored deliberately from `apps/api/src/domain/capacity.ts`.
 * There, the union exists because `row?.limit ?? null` is the one-character way to turn a failed
 * read into "unlimited". The same union on this side means a screen cannot render "ללא הגבלה" for a
 * number it simply failed to receive.
 */
export type CapacityLimit =
  | { readonly metered: false }
  | { readonly metered: true; readonly limit: number };

export interface CapacityUsage {
  readonly kind: CapacityKind;
  /** Rows that already exist and count against the limit, as the server counted them. */
  readonly current: number;
  readonly limit: CapacityLimit;
}

/**
 * The refusal the SERVER produced when the (N+1)th could not be created — 08 §7's "block CREATING
 * the next, never disable what already works", carrying the count, the limit and where to go.
 *
 * The UI renders this. It never computes one: a screen that decided for itself that a customer is
 * at their limit would be a second implementation of a commercial rule, and the first one is in
 * `apps/api/src/domain/capacity.ts`.
 */
export interface CapacityRefusal {
  readonly reason: 'limit_reached';
  readonly kind: CapacityKind;
  readonly current: number;
  readonly limit: number;
  /** An address inside this product, built by `routes.ts`. Never an external URL. */
  readonly upgradeHref: string;
}

export interface SubscriptionSummary {
  readonly subscriptionUid: string;
  readonly state: SubscriptionState;
  readonly planNameHe: string;
  readonly periodStartUtc: string;
  readonly periodEndUtc: string;
  readonly trialEndsAtUtc: string | null;
  /** Module codes bought. Never a SKU — 08 D12: the gate never sees one. */
  readonly licensedModules: readonly string[];
  readonly capacity: readonly CapacityUsage[];
}

// ── Audit (04 §8.2) ───────────────────────────────────────────────────────────────────────────────

export const AUDIT_OUTCOMES = ['allow', 'deny'] as const;
export type AuditOutcome = (typeof AUDIT_OUTCOMES)[number];

export const AUDIT_ACTOR_KINDS = [
  'user',
  'service',
  'job',
  'anonymous',
  'platform_support',
] as const;
export type AuditActorKind = (typeof AUDIT_ACTOR_KINDS)[number];

export interface AuditEventSummary {
  readonly eventUid: string;
  readonly occurredAtUtc: string;
  readonly actorKind: AuditActorKind;
  readonly actorDisplayName: string | null;
  /** In office Hebrew. 09 §9.1 V3: never a route, never a handler name, never a status code. */
  readonly actionHe: string;
  readonly outcome: AuditOutcome;
  readonly denyReason: DenyReason | null;
  /** Non-null ⇒ the action happened under a platform support grant, and that is never hidden. */
  readonly supportGrantUid: string | null;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// SOURCE CONNECTIONS — the vocabulary of `packages/sources`
//
//   STALENESS_STATES / PARTIAL_REASONS / EMPTY_REASONS / CANONICAL_ORIGINS
//                                        ←  packages/sources/src/canonical.ts
//   CONNECTOR_STATUSES                   ←  packages/sources/src/binding.ts
//   SOURCE_FAILURE_KINDS                 ←  packages/sources/src/adapter.ts (`SourceFailure`)
//
// Nothing here is invented. 07 §6.2 already separates the two watermarks the product must never
// confuse — `dataAsOfUtc`, the latest business date present in the data, and `fetchedAtUtc`, when we
// last successfully talked to the source — because a successful pull that returns nothing advances
// the second and MUST NOT advance the first. That is the mechanism that let a canonical freeze stay
// invisible for a month, and it is why the freshness line on the screen quotes the DATA watermark.
// ══════════════════════════════════════════════════════════════════════════════════════════════════

export const STALENESS_STATES = ['FRESH', 'LAGGING', 'STALE', 'UNKNOWN'] as const;
export type StalenessState = (typeof STALENESS_STATES)[number];

export const PARTIAL_REASONS = [
  'unbalanced',
  'rows_rejected_at_boundary',
  'page_limit_reached',
  'value_absent_in_set',
] as const;
export type PartialReason = (typeof PARTIAL_REASONS)[number];

export const EMPTY_REASONS = [
  'no_data_for_period',
  'capability_not_supplied',
  'source_returned_nothing',
] as const;
export type EmptyReason = (typeof EMPTY_REASONS)[number];

export type CompletenessSummary =
  | { readonly kind: 'COMPLETE' }
  | { readonly kind: 'PARTIAL'; readonly reason: PartialReason }
  | { readonly kind: 'EMPTY'; readonly reason: EmptyReason };

/**
 * `live_source` vs `development_fixture` — the one branch a screen is invited to make, and the
 * specific defence against a demo surface being mistaken for a proven integration.
 */
export const CANONICAL_ORIGINS = ['live_source', 'development_fixture'] as const;
export type CanonicalOrigin = (typeof CANONICAL_ORIGINS)[number];

export const CONNECTOR_STATUSES = [
  'PROVISIONING',
  'ACTIVE',
  'SUSPENDED',
  'FAILED',
  'REVOKED',
] as const;
export type ConnectorStatus = (typeof CONNECTOR_STATUSES)[number];

export const SOURCE_FAILURE_KINDS = [
  'NOT_CONFIGURED',
  'NOT_PROVEN',
  'UNREACHABLE',
  'NOT_AUTHENTICATED',
  'NOT_AUTHORIZED_AT_SOURCE',
  'CAPABILITY_NOT_SUPPLIED',
  'EXTERNAL_COMPANY_UNKNOWN',
  'REJECTED_AT_BOUNDARY',
  'ADAPTER_FAULT',
] as const;
export type SourceFailureKind = (typeof SOURCE_FAILURE_KINDS)[number];

/**
 * The three states the connections list shows, and the first one is the one that matters.
 *
 * `NOT_CONFIGURED` and `ERROR` are DIFFERENT ANSWERS to different questions — "this was never set
 * up" against "this was set up and it is broken" — and 07 N-5 is why they may never collapse: a
 * missing `data_source_type` silently became a working default on 5 of 313 companies, and those
 * five behaved as though they were configured. One amber "problem" chip is that defect in a nicer
 * font.
 *
 * The state is STATED BY THE SERVER, alongside the richer `failureKind` it derived it from. This
 * app renders it. It does not recompute it from the other fields.
 */
export const SOURCE_CONNECTION_STATES = ['NOT_CONFIGURED', 'OK', 'ERROR'] as const;
export type SourceConnectionState = (typeof SOURCE_CONNECTION_STATES)[number];

/**
 * 07 §6.2 F-1..F-5. `null` is the honest answer for a connection that has never observed anything;
 * it is never rendered as a zero, an epoch date, or "now".
 */
export interface FreshnessSummary {
  readonly state: StalenessState;
  /** The latest BUSINESS date present in the data. This is the one the user is shown. */
  readonly dataAsOfUtc: string | null;
  /** When we last successfully talked to the source. Never the headline: a zero-row pull moves it. */
  readonly fetchedAtUtc: string | null;
  /** From the schedule + SLA. `null` ⇒ no basis for a verdict, which is `UNKNOWN` and not `FRESH`. */
  readonly expectedByUtc: string | null;
}

export interface SourceConnectionSummary {
  readonly connectionUid: string;
  /** What the office calls this connection. Never a host, never a connection string (07 §2.3). */
  readonly displayNameHe: string;
  readonly state: SourceConnectionState;
  readonly connectorStatus: ConnectorStatus;
  readonly origin: CanonicalOrigin;
  /** Present on `ERROR` and on `NOT_CONFIGURED`; `null` on `OK`. */
  readonly failureKind: SourceFailureKind | null;
  /**
   * EVERY absent configuration field, not the first — an operator fixing one at a time is an
   * operator who runs the probe five times (07 N-5).
   */
  readonly missingConfiguration: readonly string[];
  /** `null` ⇒ no watermark exists yet, which a never-configured connection must say in words. */
  readonly freshness: FreshnessSummary | null;
  readonly completeness: CompletenessSummary;
  /** How many companies in this office are bound to this connection, as the server counted them. */
  readonly boundCompanyCount: number;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// SECURITY / MFA — `packages/authn`
// ══════════════════════════════════════════════════════════════════════════════════════════════════

/** `packages/authn/src/totp.ts` — RFC 6238 parameters, restated so a screen can label its fields. */
export const TOTP_DIGITS = 6;
export const TOTP_TIME_STEP_SECONDS = 30;
export const RECOVERY_CODE_COUNT = 10;

export const TENANT_MFA_POLICIES = ['optional', 'required_for_admins', 'required_for_all'] as const;
export type TenantMfaPolicy = (typeof TENANT_MFA_POLICIES)[number];

/**
 * What the enrolment screen is handed.
 *
 * Note what is NOT here: no password, ever (04 §2.2 — "no path may ever produce, transmit or
 * display a password"), and no QR image this app draws for itself. The provisioning URI is built by
 * the server from an issuer that is CONFIGURATION; a product name written into this tree would be
 * baked into every enrolled device, not merely into the source.
 */
export interface MfaEnrolmentOffer {
  /** Base32, shown in groups so it can be typed by hand when no camera is available. */
  readonly secretBase32: string;
  /** The account label the authenticator will show. Supplied by the server. */
  readonly accountLabel: string;
  /** Rendered by the server; absent until it is. The screen says so rather than drawing a fake one. */
  readonly qrImageDataUri: string | null;
  /** Shown ONCE. `packages/authn/src/recovery-codes.ts` stores only their hashes. */
  readonly recoveryCodes: readonly string[];
  readonly policy: TenantMfaPolicy;
  /** `product_floor` for a platform account — no tenant policy can lower it. */
  readonly requiredBy: 'product_floor' | 'tenant_policy' | 'not_required';
}

/** The three answers `verifyTotp` can give for a rejected code, plus acceptance. */
export const MFA_CHALLENGE_OUTCOMES = ['accepted', 'malformed', 'incorrect', 'replayed'] as const;
export type MfaChallengeOutcome = (typeof MFA_CHALLENGE_OUTCOMES)[number];

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// PRODUCT STATES THE SERVER ALREADY KNOWS
//
// ── WHY THIS SECTION EXISTS ───────────────────────────────────────────────────────────────────────
//
// Owner direction, verbatim: *"Do not collapse materially different states into generic 403/409/500
// when the server already knows the actionable reason."*
//
// Every type below is a state the server COMPUTED and put on the wire, and each one is a state that
// a generic error would have destroyed:
//
//   · a capacity refusal carries which capacity, the count, the limit and where to go — 08 §7;
//   · a role-assignment refusal carries `excess`, the capabilities that could not be granted;
//   · an invitation answers 202 with `delivery` when the mail port suppressed or failed the message,
//     because *"a caller who is told 200 when nobody receives anything has no way to tell that from a
//     spam folder"*;
//   · MFA enrolment answers 403 with `need`, which is disclosable — the caller is already
//     authenticated, and naming the challenge is the difference between a product and a locked door;
//   · a lifecycle refusal carries the CURRENT status and the permitted moves FROM WHERE THE OBJECT
//     ACTUALLY IS, so a screen offers the three buttons that work instead of printing "refused";
//   · a source connection separates NOT CONFIGURED from ERROR, which 07 N-5 measured the cost of.
//
// ── AND WHAT THEY ARE NOT ─────────────────────────────────────────────────────────────────────────
//
// None of these is a `DenyBody`. The gate had already ALLOWED the request when they were produced —
// the caller holds the capability and is inside their tenant. They describe why the WRITE could not
// happen, which is a different question from who may ask, and they travel as conflicts (or as a 202)
// for exactly that reason. Nothing on this side evaluates one.
//
// Every reason string is mirrored from the file that produces it, and each mapper module's test reads
// that file off disk — the same discipline as `contract.mirror.test.ts`, applied per state.
// ══════════════════════════════════════════════════════════════════════════════════════════════════

// ── Mail delivery (packages/mail/src/port.ts) ─────────────────────────────────────────────────────

/**
 * `sent · suppressed · failed`, and they are three outcomes rather than a boolean on purpose:
 * `port.test.ts` exists to keep them distinguishable. `suppressed` is the development sink and any
 * deliberate hold; `failed` is a provider that refused. The office needs different words for each.
 */
export const MAIL_OUTCOMES = ['sent', 'suppressed', 'failed'] as const;
export type MailOutcome = (typeof MAIL_OUTCOMES)[number];

/**
 * What the 202 body's `delivery` field carries. The server builds it as `` `mail_${outcome}` ``, so
 * the only values that can ever arrive are the two non-accepted outcomes.
 */
export const MAIL_DELIVERY_REASONS = ['mail_suppressed', 'mail_failed'] as const;
export type MailDeliveryReason = (typeof MAIL_DELIVERY_REASONS)[number];

/**
 * An invitation that was WRITTEN and whose message did not go out.
 *
 * The invitation exists — `invitationUid` is real, the token was minted, the row is in the database.
 * What did not happen is the delivery, and 202 is the honest status for exactly that: accepted, not
 * completed. `null` delivery is the 200 path, where the message was accepted by the provider.
 */
export interface InvitationResult {
  readonly invitationUid: string;
  /** `null` ⇒ accepted for delivery. Otherwise the office must be told, and told which. */
  readonly delivery: MailDeliveryReason | null;
  /** When the invitation stops resolving. A resend mints a NEW token and this moves. */
  readonly expiresAtUtc: string | null;
}

// ── Step-up (apps/api/src/services/authentication.ts) ─────────────────────────────────────────────

/**
 * The three challenges a step-up can require. Disclosable by design: the caller is already
 * authenticated, so naming WHICH one is safe and is the whole of the difference between an
 * actionable screen and a 403.
 *
 *   REAUTH    the session is too old for this action — sign in again
 *   MFA       a second factor is enrolled and has not been presented in this session
 *   ENROLMENT no second factor exists yet, and this action requires one
 */
export const STEP_UP_NEEDS = ['REAUTH', 'MFA', 'ENROLMENT'] as const;
export type StepUpNeed = (typeof STEP_UP_NEEDS)[number];

// ── Lifecycle refusals ────────────────────────────────────────────────────────────────────────────

/**
 * A tenant lifecycle action that could not be applied — `refuse()` in `routes/commercial.ts`.
 *
 * `currentStatus` and `permittedFrom` are the actionable half. "Refused" tells an operator nothing;
 * "this customer is already suspended" tells them the job is done, and "suspension is permitted from
 * active or trial" tells them what would have to change first.
 */
export interface TenantLifecycleRefusal {
  readonly reason: string;
  /** Where the tenant actually is. Absent when the refusal is not about a status at all. */
  readonly currentStatus: TenantStatus | null;
  /** The statuses the attempted move is permitted FROM. Empty when the refusal is not a transition. */
  readonly permittedFrom: readonly TenantStatus[];
}

/**
 * A company lifecycle transition that could not be applied — `companies.company.lifecycle`.
 *
 * `allowedTransitions` is *"the actionable half: a screen can offer the three buttons that work
 * instead of printing refused"*, in the route's own words. It is the list FROM WHERE THE COMPANY
 * ACTUALLY IS, which is why it can differ from what the screen offered a moment ago.
 */
export interface CompanyTransitionRefusal {
  readonly reason: string;
  readonly from: CompanyLifecycleStatus | null;
  readonly allowedTransitions: readonly CompanyLifecycleStatus[];
}

// ── Sessions (packages/authn/src/session-secret.ts) ───────────────────────────────────────────────

export const SESSION_REVOCATION_REASONS = [
  'user_signed_out',
  'password_changed',
  'mfa_changed',
  'recovery_code_used',
  'membership_disabled',
  'administrator_revoked',
  /**
   * The TENANT was terminated, which is a different fact from a person acting on one account —
   * `administrator_revoked` names an actor and a scope that would both be wrong for an offboarding
   * cascade. `packages/connector` already had `tenant_closed`, and the two halves of one event should
   * not be describable in only one of them.
   */
  'tenant_terminated',
  'suspected_compromise',
] as const;
export type SessionRevocationReason = (typeof SESSION_REVOCATION_REASONS)[number];

/**
 * The three an ADMINISTRATOR may assert — `ADMIN_ASSERTABLE_REVOCATION_REASONS`, which the server
 * DERIVES (everything not automatic, minus `user_signed_out`) rather than hand-listing.
 *
 * The rest are facts the system observes. An administrator who could assert `password_changed` would
 * be writing a password change that never happened into the audit trail; one who could assert
 * `user_signed_out` would be attributing an eviction to the person it was performed on. The service
 * refuses anything outside this set and returns the set itself as the `excess`.
 */
export const ADMIN_ASSERTABLE_REVOCATION_REASONS: readonly SessionRevocationReason[] = [
  'administrator_revoked',
  'tenant_terminated',
  'suspected_compromise',
];

/**
 * One row of "where this account is signed in".
 *
 * There is no IP address and no user-agent string here, and their absence is the design: this list is
 * read by an administrator ABOUT A COLLEAGUE (`tenant.user.session.read`, audited ALWAYS because
 * reading it is surveillance of a person), and the question it has to answer is "how many, since
 * when, and can I evict this one" — not "where does this person work from".
 */
export interface UserSessionSummary {
  readonly sessionUid: string;
  readonly startedAtUtc: string;
  readonly lastSeenAtUtc: string | null;
  readonly expiresAtUtc: string | null;
  /** `true` for the session making the request. Only ever set on the caller's OWN list. */
  readonly current: boolean;
  /** Non-null ⇒ already revoked, and the row stays visible with the reason (02 §8: no delete). */
  readonly revokedReason: SessionRevocationReason | null;
}

// ── Connector credentials (packages/connector/src/credential.ts) ──────────────────────────────────

export const CREDENTIAL_REVOCATION_REASONS = [
  'suspected_compromise',
  'operator_revoked',
  'connector_decommissioned',
  'tenant_closed',
  'enrolment_disputed',
  'rotation_forced',
] as const;
export type CredentialRevocationReason = (typeof CREDENTIAL_REVOCATION_REASONS)[number];

/**
 * The two that mean PAST traffic is in question, not merely that future traffic stops.
 * A revoked credential says different things to an office depending on which side of this line it is
 * on, and one sentence for both would be the collapse this whole section exists against.
 */
export const RETROSPECTIVE_REVOCATION_REASONS: readonly CredentialRevocationReason[] = [
  'suspected_compromise',
  'enrolment_disputed',
];

// ── Connector health (packages/connector/src/health.ts) ───────────────────────────────────────────

/**
 * CONTACT health, and `NEVER_CONNECTED` is not a kind of `DISCONNECTED`.
 *
 * The first is an installation problem on the customer's machine; the second worked yesterday and is
 * an operations problem. Same colour on a dashboard, different person, different hour — and roughly
 * 24,000 items queued with nothing ever ingested is what one chip for both was worth.
 */
export const CONNECTOR_HEALTH_STATES = [
  'NEVER_CONNECTED',
  'UNMEASURABLE',
  'CONNECTED',
  'DEGRADED',
  'DISCONNECTED',
] as const;
export type ConnectorHealthState = (typeof CONNECTOR_HEALTH_STATES)[number];

export const DEGRADATION_CODES = [
  'CREDENTIAL_ROTATION_DUE',
  'PROTOCOL_VERSION_SUNSETTING',
  'CLOCK_SKEW_NEAR_WINDOW',
  'SOURCE_UNREACHABLE',
  'SOURCE_REFUSING_REQUESTS',
  'BINDINGS_UNMAPPED',
] as const;
export type DegradationCode = (typeof DEGRADATION_CODES)[number];

/**
 * `contactIsNotDataFreshness` is always `true` and is carried so a consumer cannot claim it was not
 * told. A green connector above a stale trial balance is the June 2026 freeze exactly, and this
 * payload therefore has no data watermark on it at all.
 */
export interface ConnectorHealthSummary {
  readonly connectionUid: string;
  readonly state: ConnectorHealthState;
  readonly lastSeenAtUtc: string | null;
  readonly degradations: readonly DegradationCode[];
  readonly contactIsNotDataFreshness: true;
}

// ── Source administration refusals (apps/api/src/services/source-admin.ts) ────────────────────────

/**
 * A configuration that was refused. `missingConfiguration` names EVERY absent field and never the
 * first — 07 N-5: an operator fixing one at a time is an operator who runs the probe five times.
 */
export interface SourceConfigurationRefusal {
  readonly reason: string;
  readonly missingConfiguration: readonly string[];
  /** The service's own sentence. Rendered only when this build has no Hebrew for `reason`. */
  readonly remedy: string | null;
}

/**
 * The result of a connectivity check. 200 EVEN WHEN THE SOURCE FAILED: a probe that RAN produced an
 * answer, and turning "the customer's system is off" into a 5xx makes it indistinguishable from "our
 * product is broken". `diagnostic` is deliberately absent from this mirror — it is for an operator
 * and for the audit trail, no caller may parse one, and the screen renders the KIND.
 */
export type ProbeOutcome =
  | { readonly ok: true; readonly checkedAtUtc: string | null }
  | { readonly ok: false; readonly failureKind: SourceFailureKind };

/**
 * How the date arrives from the source, and how strong an identifier assertion is.
 *
 * Both are the SERVER'S closed lists, mirrored because a mapping form has to offer them. There is no
 * "guess" member in either: 07 §2.3 makes the date format part of the connection's configuration
 * rather than something an importer sniffs per file, and `RESOLVABLE_CONFIDENCES` on the server side
 * is what decides which assertions may be acted on — never a screen.
 */
export const SOURCE_DATE_FORMATS = ['ISO_8601', 'DAY_FIRST_SLASH', 'COMPACT_YYYYMMDD'] as const;
export type SourceDateFormat = (typeof SOURCE_DATE_FORMATS)[number];

export const IDENTIFIER_CONFIDENCES = [
  'authoritative',
  'human_confirmed',
  'derived_verified',
  'legacy_unverified',
  'proposed',
  'disputed',
] as const;
export type IdentifierConfidence = (typeof IDENTIFIER_CONFIDENCES)[number];

/** One company as the SOURCE names it. Never normalised — 05 R-5: `a049` and `a49` are two clients. */
export interface ExternalCompanySummary {
  /** Opaque text in the source's namespace. Shown LTR, never trimmed, never case-folded. */
  readonly externalCompanyId: string;
  readonly displayName: string;
  /** The internal company it is bound to, or `null` — which is the onboarding queue, as a state. */
  readonly boundCompanyUid: string | null;
}

/**
 * Why a binding could not be made or resolved.
 *
 * `not_found` deliberately covers an unknown connector AND a cross-tenant one together (S-12): a
 * caller able to tell them apart can enumerate another office's connectors one probe at a time. The
 * collapse is the SERVER'S, made once in `binding.ts`, and this side renders whichever answer arrived.
 */
export const BINDING_REFUSALS = ['not_found', 'unmapped', 'ambiguous', 'unavailable'] as const;
export type BindingRefusalReason = (typeof BINDING_REFUSALS)[number];

export interface BindingRefusal {
  readonly reason: BindingRefusalReason;
  /**
   * 0002 rule 3: *a conflict that cannot say what would settle it is a conflict nobody can close.*
   * `null` only where the refusal is not a conflict between candidates.
   */
  readonly resolvingFactRequired: string | null;
  /** How many internal companies claim this external one. Never WHICH — those are internal ids. */
  readonly candidateCount: number;
}

// ── Support grants (04 §13.1.5 / §6.4) ────────────────────────────────────────────────────────────

export const SUPPORT_GRANT_MODES = ['READ_ONLY', 'READ_WRITE'] as const;
export type SupportGrantMode = (typeof SUPPORT_GRANT_MODES)[number];

/**
 * What still blocks a grant from being usable. A grant is created UNAPPROVED and, where the customer
 * requires it, WITHOUT CONSENT — so the ordinary state of a fresh grant is "blocked by two things",
 * and a screen that showed only "pending" would hide which of them the operator can act on.
 */
export const SUPPORT_GRANT_BLOCKERS = [
  'awaiting_approval',
  'awaiting_consent',
  'expired',
  'revoked',
] as const;
export type SupportGrantBlocker = (typeof SUPPORT_GRANT_BLOCKERS)[number];

export interface SupportGrantSummary {
  readonly grantUid: string;
  /** The customer the grant reaches into. Named, always — a grant with no named tenant is a key. */
  readonly tenantUid: string;
  readonly tenantLegalName: string;
  readonly requestedByDisplayName: string;
  readonly reason: string;
  readonly mode: SupportGrantMode;
  readonly startsAtUtc: string;
  readonly expiresAtUtc: string;
  /** Empty ⇒ the grant is live. Anything in it means the gate is still refusing. */
  readonly blockedBy: readonly SupportGrantBlocker[];
  /**
   * OA-0002: a falsifiable assertion attributable to a named requester, never a defaulted note.
   * Shown because a related-party support access is the one an auditor will ask about first.
   */
  readonly isRelatedParty: boolean;
}

/**
 * ONE SUPPORT GRANT, AS THE OFFICE SEES IT. **CM-012.**
 *
 * The tenant-side counterpart of `SupportGrantSummary` above, and the differences between the two
 * are the whole point. This one names no tenant — there is only one it could be, and a field for it
 * would be a field somebody could put another value in. It carries no requester and no approval
 * trail: who at GL-POINT asked for the grant and which second principal approved it is OUR internal
 * governance, not a fact about the customer.
 *
 * What it does carry is everything the office needs to act: why, for how long, whether its own
 * consent is outstanding, and whether it has already revoked.
 */
export interface TenantSupportGrantView {
  readonly grantUid: string;
  readonly reason: string;
  readonly ticketRef: string | null;
  readonly mode: SupportGrantMode;
  /** True ⇒ the grant is dead until this office consents, whatever GL-POINT has approved. */
  readonly requiresConsent: boolean;
  readonly consentGivenAtUtc: string | null;
  readonly startsAtUtc: string;
  readonly expiresAtUtc: string;
  readonly revokedAtUtc: string | null;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// OFFBOARDING — what happens when a customer leaves.
//
//   OFFBOARDING_STAGES / Reversal / OffboardingStage / RevocationTally / PurgeEligibility
//   RetentionPolicySummary / ExportDisposition   ←  apps/api/src/services/tenant-lifecycle.ts
//   RESIDENCY_CATEGORIES                         ←  packages/sources/src/residency.ts
//
// ── WHY THIS MIRROR IS A UNION AND NOT A FLAG ─────────────────────────────────────────────────────
//
// The service is emphatic, and the shape is the argument: *"A boolean cannot carry the one thing a
// caller must know before acting: what UNDOING this costs."* A screen that reduced `Reversal` to
// `canUndo: boolean` would answer `false` for a tenant whose reversibility is simply NOT KNOWABLE —
// there is no readable retention policy — and an operator reading "cannot be undone" about a
// customer terminated in error would go and re-create them as a new tenant, which loses the audit
// thread, the company identity graph and the conflict history that reinstatement would have kept.
//
// So the union is mirrored member for member, `ACTIVE` carries NO `reversal` key at all, and
// `offboardingText.ts` gives each member its own Hebrew. `contract.mirror.test.ts` reads the service
// off disk and fails if a member is added, removed or renamed there and not here.
//
// ── AND THE ABSENCE THAT IS THE POINT ─────────────────────────────────────────────────────────────
//
// There is NO `purge` action type here, because there is no purge ROUTE and no capability that could
// authorise one. `PurgeEligibility` is an ANSWER: it says whether the governed deletion workflow may
// begin and, when it may not, exactly what is blocking it. `eligible: true` is not permission to
// delete — it is the removal of the offboarding service's objections.
// ══════════════════════════════════════════════════════════════════════════════════════════════════

export const OFFBOARDING_STAGES = ['ACTIVE', 'SUSPENDED', 'TERMINATED', 'PURGED'] as const;
export type OffboardingStageName = (typeof OFFBOARDING_STAGES)[number];

export const REVERSAL_KINDS = [
  'FREE',
  'GOVERNED_WINDOW',
  'CLOSED',
  'UNDETERMINABLE',
  'IRREVERSIBLE',
] as const;
export type ReversalKind = (typeof REVERSAL_KINDS)[number];

/**
 * WHY reversibility is not assertable. Two different configuration failures, and the screen must
 * name which — "there is no policy" is fixed by writing one, "the policy does not hold together" is
 * fixed by correcting the one that exists.
 */
export const UNDETERMINABLE_REASONS = [
  'retention_policy_absent',
  'retention_policy_invalid',
] as const;
export type UndeterminableReason = (typeof UNDETERMINABLE_REASONS)[number];

/**
 * WHAT UNDOING THIS STAGE COSTS.
 *
 * `restores: 'nothing'` on `GOVERNED_WINDOW` is not a placeholder and the screen must not soften it:
 * reinstatement gives back the RELATIONSHIP and not one unit of access.
 */
export type Reversal =
  | { readonly kind: 'FREE' }
  | { readonly kind: 'GOVERNED_WINDOW'; readonly until: string; readonly restores: 'nothing' }
  | { readonly kind: 'CLOSED'; readonly because: 'reinstatement_window_closed' }
  | { readonly kind: 'UNDETERMINABLE'; readonly because: UndeterminableReason }
  | { readonly kind: 'IRREVERSIBLE' };

/**
 * WHAT BECAME UNREACHABLE — a count per class, never a list.
 *
 * Counts and not identities, for the service's own reason: a list of session uids or credential
 * fingerprints in an immutable audit row is a durable disclosure of exactly the material the session
 * cookie was split to avoid printing.
 */
export interface RevocationTally {
  readonly connectorCredentials: number;
  readonly connectors: number;
  readonly sourceConnections: number;
  readonly invitations: number;
  readonly sessions: number;
}

/**
 * The five classes, IN THE ORDER THE SERVER REVOKES THEM — decreasing autonomy: the unattended
 * machine first, the people last. The screen lists them in this order because that order is the
 * answer to "what happened first", and re-sorting it alphabetically would destroy the one property
 * the list carries beyond its numbers.
 */
export const REVOCATION_CLASSES = [
  'connectorCredentials',
  'connectors',
  'sourceConnections',
  'invitations',
  'sessions',
] as const;
export type RevocationClass = (typeof REVOCATION_CLASSES)[number];

/**
 * The stage, as a value that CANNOT BE READ WITHOUT NARROWING.
 *
 * `ACTIVE` has no `reversal` key, so a screen cannot ask "is this reversible?" of a stage where the
 * question is meaningless and get `false` by accident. `revoked` appears on `TERMINATED` and nowhere
 * else, because only termination makes anything unreachable.
 */
export type OffboardingStage =
  | { readonly stage: 'ACTIVE' }
  | {
      readonly stage: 'SUSPENDED';
      readonly reversal: Extract<Reversal, { kind: 'FREE' }>;
      readonly enteredAt: string;
      readonly reason: string;
    }
  | {
      readonly stage: 'TERMINATED';
      readonly reversal: Reversal;
      readonly enteredAt: string;
      readonly reason: string;
      readonly revoked: RevocationTally;
    }
  | {
      readonly stage: 'PURGED';
      readonly reversal: Extract<Reversal, { kind: 'IRREVERSIBLE' }>;
      readonly enteredAt: string;
      readonly reason: string;
    };

/** Everything the governed deletion workflow is waiting for. Never one blocker only. */
export const PURGE_BLOCKERS = [
  'not_terminated',
  'already_purged',
  'retention_policy_absent',
  'retention_policy_invalid',
  'retention_period_running',
  'export_obligation_outstanding',
] as const;
export type PurgeBlocker = (typeof PURGE_BLOCKERS)[number];

export type PurgeEligibility =
  | {
      readonly eligible: false;
      readonly blockedBy: readonly PurgeBlocker[];
      /** Present when the only thing standing in the way is the clock. */
      readonly notBefore?: string;
    }
  | {
      readonly eligible: true;
      readonly notBefore: string;
      /** `describeResidency`'s sentence. Named in Hebrew by a mapper; never rendered verbatim. */
      readonly residency: string;
      /** What the policy's author stated deletion would mean for this customer's data. */
      readonly deletionImplication: string;
    };

/**
 * Every way a retention policy can fail to hold together. The residency half arrives as
 * `` `residency_${violation}` `` from the sources package, which is an open suffix — so the type is a
 * union with a template member and the mapper answers `null` for one this build has no words for.
 */
export const RETENTION_POLICY_VIOLATIONS = [
  'basis_blank',
  'deletion_eligibility_not_a_day_count',
  'reinstatement_window_not_a_day_count',
  'reinstatement_window_outlives_retention',
] as const;
export type RetentionPolicyViolation =
  | (typeof RETENTION_POLICY_VIOLATIONS)[number]
  | `residency_${string}`;

/** Mirrored so a screen can name the category rather than print the server's boot-report sentence. */
export const RESIDENCY_CATEGORIES = ['LIVE_READ', 'CACHE', 'SNAPSHOT', 'GLPOINT_OWNED'] as const;
export type ResidencyCategory = (typeof RESIDENCY_CATEGORIES)[number];

/**
 * The policy as a screen may see it.
 *
 * NOTICE WHAT IS ABSENT: no company, no user, no amount, no ledger figure. And notice that there is
 * no day count with a default — `deletionEligibleAfterDays` is CONFIGURED, and a `null` policy is
 * rendered as the absence it is rather than as an unconstrained one.
 */
export interface RetentionPolicySummary {
  readonly policyUid: string;
  readonly policyVersion: number;
  /** The governing instrument, in the words of whoever set it. Rendered verbatim; it is their text. */
  readonly basis: string;
  readonly deletionEligibleAfterDays: number;
  readonly reinstatementWindowDays: number;
  readonly residency: string;
  readonly setAt: string;
  /** Every reason the policy does not hold together. Empty ⇒ it does. */
  readonly violations: readonly RetentionPolicyViolation[];
}

/** The read model of `platform.tenant.offboarding.read`. Operational metadata, never business data. */
export interface OffboardingStateView {
  readonly stage: OffboardingStage;
  /** `null` ⇒ no governing policy. Rendered as the absence it is, never as a default. */
  readonly retention: RetentionPolicySummary | null;
  readonly purge: PurgeEligibility;
}

/**
 * WHAT HAPPENS TO THE CUSTOMER'S DATA EXPORT — declared, never defaulted.
 *
 * The route parses this and answers a malformed or absent one as a bad request; it does NOT become
 * `WAIVED`. The customer's right to their data survives termination, and *"a right that disappears
 * when a field is omitted is not a right, it is a note"* — so the form has no pre-selected answer.
 */
export const EXPORT_DISPOSITION_KINDS = ['REQUESTED', 'ALREADY_DELIVERED', 'WAIVED'] as const;
export type ExportDispositionKind = (typeof EXPORT_DISPOSITION_KINDS)[number];

export type ExportDisposition =
  | { readonly kind: 'REQUESTED' }
  | { readonly kind: 'ALREADY_DELIVERED'; readonly exportRequestUid: string }
  | { readonly kind: 'WAIVED'; readonly detail: string };

/**
 * WHAT A REINSTATED TENANT HAS TO DO AGAIN. Never empty on a successful reinstatement.
 *
 * `subscription_state` is on it because reinstatement leaves the subscription expired ON PURPOSE:
 * restoring service is a separate commercial decision, and a reinstatement that silently restored it
 * would be a commercial decision made by an offboarding routine.
 */
export const MUST_REESTABLISH_ITEMS = [
  'connector_enrolment',
  'source_connection_credentials',
  'user_sessions',
  'invitations',
  'subscription_state',
] as const;
export type MustReestablishItem = (typeof MUST_REESTABLISH_ITEMS)[number];

/**
 * AN OFFBOARDING ACTION THAT COULD NOT BE APPLIED — `refuse()` in `routes/tenant-lifecycle.ts`.
 *
 * Every field is the actionable half of one refusal, and `revoked` is the one that matters most:
 * *"a cascade that revoked five classes of access and then failed to record its stage returns a
 * refusal that STILL CARRIES THE TALLY, because an operator told only 'concurrent_modification'
 * would not know that every session in the tenant is gone."* A screen that dropped it would show a
 * generic conflict over a tenant whose connector credentials are already dead.
 */
export interface OffboardingRefusal {
  readonly reason: string;
  /** Where the tenant actually is. `null` when the refusal is not about a stage. */
  readonly currentStage: OffboardingStageName | null;
  /** The stages the tenant may move to FROM WHERE IT ACTUALLY IS. Empty when not a transition. */
  readonly allowedStages: readonly OffboardingStageName[];
  /** Names the service that DOES own the arc that was asked for. A code, never rendered raw. */
  readonly arcOwner: string | null;
  /** Every reason the retention policy did not hold together, not the first one. */
  readonly violations: readonly RetentionPolicyViolation[];
  readonly reinstatableUntil: string | null;
  readonly notBefore: string | null;
  /** The refusal `CommercialService` produced, carried through rather than flattened to "refused". */
  readonly commercialReason: string | null;
  /** NON-NULL ⇒ the cascade ran before the refusal. Access is already gone. */
  readonly revoked: RevocationTally | null;
}
